import { v4 as uuid } from 'uuid'
import { computed, ref } from 'vue'

import type { CanvasHistoryItem, ComponentNode } from '@canvas-builder/types/canvas'
import { ComponentIcon, CanvasHistoryAction, ComponentTypes } from '@canvas-builder/types/canvas'
import { simpleDeepClone } from '@canvas-builder/util/clone'
import useCanvas from '@canvas-builder/composables/useCanvas'

const canvasHistory = ref<CanvasHistoryItem[]>([])

const addCanvasHistoryItem = ({
  action,
  componentType,
}: {
  action: CanvasHistoryAction
  componentType: ComponentNode['type']
}) => {
  const { canvasContent } = useCanvas()

  if (activeHistoryItemIndex.value !== -1 && activeHistoryItemIndex.value < canvasHistory.value.length - 1) {
    canvasHistory.value = canvasHistory.value.slice(0, activeHistoryItemIndex.value + 1)
  }

  canvasHistory.value.forEach((i) => {
    i.active = false
  })

  canvasHistory.value.push({
    action,
    active: true,
    id: uuid(),
    icon:
      componentType === ComponentTypes.Root
        ? 'presentation-screen'
        : ComponentIcon[componentType as keyof typeof ComponentIcon],
    snapshot: simpleDeepClone(canvasContent.value),
    componentType,
    timestamp: new Date().toISOString(),
  })
}

const activeHistoryItemIndex = computed(() => canvasHistory.value.findIndex((i) => i.active))

const applyHistoryItem = (id: string) => {
  const { setCanvasContent } = useCanvas()

  const foundItemIndex = canvasHistory.value.findIndex((i) => i.id === id)

  if (foundItemIndex === -1) {
    console.error('Could not find history item')
    return
  }

  canvasHistory.value.forEach((item, index) => {
    item.active = index === foundItemIndex
  })

  setCanvasContent({ content: canvasHistory.value[foundItemIndex].snapshot })
}

const redoCanvasHistoryItem = () => {
  const { setCanvasContent } = useCanvas()
  const activeItemIndex = canvasHistory.value.findIndex((i) => i.active)
  const nextItemIndex = activeItemIndex + 1 < canvasHistory.value.length ? activeItemIndex + 1 : activeItemIndex

  canvasHistory.value.forEach((item, index) => {
    item.active = index === nextItemIndex
  })

  setCanvasContent({ content: canvasHistory.value[nextItemIndex].snapshot })
}

const resetCanvasHistory = () => {
  const { canvasContent } = useCanvas()

  canvasHistory.value = [
    {
      action: CanvasHistoryAction.OPEN,
      active: true,
      id: uuid(),
      snapshot: simpleDeepClone(canvasContent.value),
      componentType: ComponentTypes.Root,
      icon: 'presentation-screen',
      timestamp: new Date().toISOString(),
    },
  ]
}

const undoCanvasHistoryItem = () => {
  const { setCanvasContent } = useCanvas()

  const activeItemIndex = canvasHistory.value.findIndex((i) => i.active)
  const previousItemIndex = activeItemIndex - 1 >= 0 ? activeItemIndex - 1 : activeItemIndex

  canvasHistory.value.forEach((item, index) => {
    item.active = index === previousItemIndex
  })

  setCanvasContent({ content: canvasHistory.value[previousItemIndex].snapshot })
}

export default function useCanvasHistory() {
  return {
    activeHistoryItemIndex,
    canvasHistory,
    addCanvasHistoryItem,
    applyHistoryItem,
    undoCanvasHistoryItem,
    redoCanvasHistoryItem,
    resetCanvasHistory,
  }
}
